import { fetchClient } from '@/api/typedClient'
import { DeleteOutlined } from '@ant-design/icons'
import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideDatasource,
  RowNode,
} from 'ag-grid-community'
import { Button, Modal, Space, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { EntityObject } from '../types'
import { getColumnDefinitions } from '../utils'
import { getEntityEndpoint, getEntityId } from '../utils/entityMappers'
import { useServerSideDataSource } from './useServerSideDataSource'

let hasPatchedForEachNode = false

const patchGridApiForEach = () => {
  if (hasPatchedForEachNode) return
  const apiPrototype = GridApi.prototype as GridApi & {
    forEachNodeAfterFilterAndSort?: (
      callback: (rowNode: RowNode, index: number) => void,
    ) => void
  }
  const originalForEach = apiPrototype.forEachNodeAfterFilterAndSort

  apiPrototype.forEachNodeAfterFilterAndSort = function (
    callback: (rowNode: RowNode, index: number) => void,
  ) {
    const model = this.getModel?.()
    const modelType =
      typeof model?.getType === 'function' ? model.getType() : undefined

    if (modelType === 'serverSide') {
      const rowCount = this.getDisplayedRowCount?.() ?? 0
      for (let index = 0; index < rowCount; index += 1) {
        const rowNode = this.getDisplayedRowAtIndex?.(index)
        if (rowNode) {
          callback(rowNode, index)
        }
      }
      return
    }

    if (originalForEach) {
      originalForEach.call(this, callback)
    }
  }

  hasPatchedForEachNode = true
}

patchGridApiForEach()

interface FilterInfo {
  searchId?: string
  entityType?: string
}

interface GridData {
  gridOptions?: unknown
  [key: string]: unknown
}

interface UseMasterDataGridProps {
  entityType: string
  gridData?: GridData
  filterInfo?: FilterInfo
  pageSize: number
}

interface UseMasterDataGridReturn {
  gridApi: GridApi | null
  totalRows: number
  columnDefs: ColDef<EntityObject>[]
  gridOptions: GridOptions<EntityObject>
  serverSideDatasource: IServerSideDatasource
  setGridApi: (api: GridApi | null) => void
  serverSearch: string
  setServerSearch: (term: string) => void
}

/**
 * Hook for managing MasterDataGrid configuration and state
 */
export const useMasterDataGrid = ({
  entityType,
  gridData,
  filterInfo,
  pageSize,
}: UseMasterDataGridProps): UseMasterDataGridReturn => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const [totalRows, setTotalRows] = useState(0)
  const [serverSearch, setServerSearch] = useState<string>('')

  // Get server-side data source
  const { serverSideDatasource } = useServerSideDataSource({
    entityType,
    onTotalRowsChange: setTotalRows,
    searchTerm: serverSearch,
    defaultPageSize: pageSize,
  })

  // Handle delete entity
  const handleDelete = useCallback(
    (entity: EntityObject) => {
      const entityId = getEntityId(entity, entityType)
      const entityName = entity.name || entityId

      Modal.confirm({
        title: 'Delete Entity',
        content: `Are you sure you want to delete "${entityName}"?`,
        okText: 'Delete',
        okType: 'danger',
        onOk: async () => {
          const endpoint = `${getEntityEndpoint(entityType)}${entityId}`
          const { error, response } = await fetchClient.DELETE(endpoint, {
            params: {
              header: { 'X-Tenant-Name': 'Gravitate' },
            },
          })
          if (error) {
            // Handle 409 Conflict - entity is in use by mappings
            if (response?.status === 409) {
              const detail =
                (error as { detail?: string }).detail ||
                'Cannot delete because it is in use by mappings'
              message.error(detail)
              return
            }
            message.error(`Failed to delete: ${JSON.stringify(error)}`)
            return
          }
          message.success(`Deleted "${entityName}"`)
          gridApi?.refreshServerSide({ purge: true })
        },
      })
    },
    [entityType, gridApi],
  )

  // Generate column definitions
  const baseColumnDefs = useMemo(() => {
    return getColumnDefinitions(entityType, gridData)
  }, [entityType, gridData])

  // Add actions column if not already present
  const columnDefs = useMemo<ColDef<EntityObject>[]>(() => {
    const hasActions = baseColumnDefs.some((col) => col.field === 'actions')
    if (hasActions) {
      return baseColumnDefs
    }

    return [
      ...baseColumnDefs,
      {
        headerName: 'Actions',
        field: 'actions',
        width: 120,
        sortable: false,
        filter: false,
        pinned: 'right',
        cellRenderer: (params: ICellRendererParams<EntityObject>) => {
          return (
            <Space size='small'>
              <Button
                size='small'
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(params.data)}
              />
            </Space>
          )
        },
      },
    ]
  }, [baseColumnDefs, handleDelete])

  // Grid options configuration
  const gridOptions = useMemo<GridOptions<EntityObject>>(
    () => ({
      columnDefs,
      defaultColDef: {
        resizable: true,
        filter: true,
      },
      animateRows: true,
      getRowId: (params: GetRowIdParams<EntityObject>) =>
        params.data.goid || params.data._id || params.data.id,
      rowSelection: 'multiple',
      headerHeight: 40,
      rowHeight: 45,
      // Server-side configuration
      rowModelType: 'serverSide' as const,
      serverSideDatasource: serverSideDatasource,
      pagination: true,
      paginationPageSize: pageSize,
      paginationPageSizeSelector: [100, 250, 500, 1000, 2500, 5000],
      cacheBlockSize: pageSize,
      cacheOverflowSize: 2,
      maxConcurrentDatasourceRequests: 1,
      infiniteInitialRowCount: pageSize,
      maxBlocksInCache: 10,
      onGridReady: (params: GridReadyEvent) => {
        setGridApi(params.api)
        params.api.paginationSetPageSize(pageSize)
        // Set datasource
        params.api.setServerSideDatasource(serverSideDatasource)
        // Apply initial filter if present
        if (filterInfo?.searchId) {
          setServerSearch(filterInfo.searchId)
        }
      },
      ...(gridData?.gridOptions || {}),
    }),
    [columnDefs, serverSideDatasource, gridData, filterInfo, pageSize],
  )

  // Apply filter when filterInfo changes
  useEffect(() => {
    if (filterInfo?.searchId) {
      setServerSearch(filterInfo.searchId)
    } else if (!filterInfo) {
      setServerSearch('')
    }
  }, [filterInfo])

  // When server search term changes, refresh SSRM and keep quick filter in sync
  useEffect(() => {
    if (gridApi) {
      gridApi.paginationGoToFirstPage()
      gridApi.refreshServerSide({ purge: true })
    }
  }, [serverSearch, gridApi])

  return {
    gridApi,
    totalRows,
    columnDefs,
    gridOptions,
    serverSideDatasource,
    setGridApi,
    serverSearch,
    setServerSearch,
  }
}
